import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import WithForcedMonospace from './WithForcedMonospace'
import { SafeFlagImg } from './SafeImgs'
import PlayerTotalAnimatedDelta from './PlayerTotalAnimatedDelta'
import WithAnimatedDeltas from './WithAnimatedDeltas'
import WithPodiumStyles from './WithPodiumStyles'

import { ZERO_WIDTH_SPACE, NO_INFORMATION_SYMBOL, SOFT_HYPHEN } from '../unicodeChars'
import { trimMs } from '../utils'

import CircleSVG from '../svgs/CircleSVG'
import CirclePlayIconSVG from '../svgs/CirclePlayIconSVG'
import FlashDiv from './FlashDiv'

import { useStore } from '../zustandStore'

// import FlipMove from 'react-flip-move'

const DEFAULT_FONT_SIZE_PX = 34

const SimpleBoardRow = forwardRef(({
  player,
  rankDigits,
  isHighlighted,
  className,
  scaleBy = 1,
  scaleByOnPhone = 1,
  onClick = () => {},
  shouldFlash,
  fullWidth = false,
  isPlaceholder,
  isLive = false,
  isFeatured,
  msDecimals = 3,
  // if this is provided, we show one chapter time instead of the total
  chapterName = null,
  // if this is provided, then we overlay this string invisibly when this is a chapter leaderboard, so chapter ranks align
  // this is a janky solution to aligning columns when different rows have different digit counts
  timeSpacerString = '',
  prependBingoPoints,
  rankOverrideKey
}, ref) => {
  const isFullGame = useStore(state => state.event.isFullGame)
  const isBingo = useStore(state => state.event.isBingo)

  const isChapterTime = !isFullGame && chapterName != null

  if (rankDigits == null) rankDigits = 2
  let wrapperClassNames = ['row']
  if (className) wrapperClassNames.push(className)

  const fontSizePx = DEFAULT_FONT_SIZE_PX * scaleBy
  const phoneFontSizePx = DEFAULT_FONT_SIZE_PX * scaleByOnPhone

  if (isPlaceholder) {
    return (
      <StyledRow isPlaceholder fullWidth={fullWidth} fontSizePx={fontSizePx} phoneFontSizePx={phoneFontSizePx} className={wrapperClassNames.join(' ')}>
        <LeftHalf>
          <Name>{ZERO_WIDTH_SPACE}</Name>
        </LeftHalf>
      </StyledRow>
    )
  }

  const mostRecentUpdatedAt = Math.max(...Object.values(player.eventBestTimesUpdatedAt))
  let mostRecentChapter, mostRecentDelta
  if (mostRecentUpdatedAt) {
    mostRecentChapter = Object.keys(player.eventBestTimesUpdatedAt)
                              .find(key => player.eventBestTimesUpdatedAt[key] === mostRecentUpdatedAt)

    mostRecentDelta = player.eventChapterDeltas[mostRecentChapter]
  }

  const rankOverride = rankOverrideKey ? player[rankOverrideKey] : null

  return (
    <StyledRow onClick={onClick} ref={ref} title={player.name} isLive={isLive} fullWidth={fullWidth} fontSizePx={fontSizePx} phoneFontSizePx={phoneFontSizePx} isHighlighted={isHighlighted} className={wrapperClassNames.join(' ')}>
      {shouldFlash && <FlashDiv />}
      {/* attempt at getting a smooth fade in/out for the live indicator despite the grid layout, oh well */}
      {/* <FlipMove typeName='div' enterAnimation='fade' leaveAnimation='fade' > */}
      <LeftHalf key='left-half'>
        <Rank className='rank' rankDigits={rankDigits}>
          {/* we show rank in IL and Full Game modes if the player has completed all chapters */}
          {/* in bingo mode, the "chapters" are boards that everyone completes at the same time together, so one chapter is enough to show rank */}
          {(player.completedAllChapters || (isBingo && player.completedAnyChapters)) &&
            <WithForcedMonospace>
              {rankOverride || player.rank}
            </WithForcedMonospace>
          }
        </Rank>

        <Name className='name'>
          {player.name}
        </Name>

        {player.isCountryPresent && (
          <Flag className='flag'>
            <SafeFlagImg {...player} />
          </Flag>
        )}
      </LeftHalf>

      {isLive
        ? isFeatured
          ? <CirclePlayIconSVG visible={isLive} className='live-icon' />
          : <CircleSVG visible={isLive} className='live-icon' />
        : null
      }
      {isChapterTime ? (
        <StyledChapterTimeWrapper className="player-total">
          {/* this is an IL mode single chapter leaderboard */}
          {player.eventBestTimes[chapterName] ? (
            <>
              {/* the player has completed this chapter, so there is a time and chapter rank to show */}
              <div className='chapter-rank'>
                <WithForcedMonospace>
                  {player.eventBestTimes[chapterName] ? player.eventChapterRanks[chapterName] : null}
                </WithForcedMonospace>
              </div>
              <GridWrapper>
                <GridCell>
                  <WithAnimatedDeltas
                    delta={player.eventChapterDeltas[chapterName]}
                    updatedAt={player.eventBestTimesUpdatedAt[chapterName]}
                    bestTime={player.eventBestTimes[chapterName]}
                  >
                    <WithPodiumStyles rank={player.eventChapterRanks[chapterName]}>
                      {trimMs(player.eventBestTimes[chapterName], msDecimals)}
                    </WithPodiumStyles>
                  </WithAnimatedDeltas>
                </GridCell>
                {/* if timeSpacerString is provided, it's rendered here */}
                {timeSpacerString && (
                  <GridCell invisible>
                    <WithForcedMonospace>
                      {trimMs(timeSpacerString, msDecimals)}
                    </WithForcedMonospace>
                  </GridCell>
                )}
              </GridWrapper>
            </>
          ) : (
            <>
              {/* the player has not completed this chapter yet, so we show a "--" and invisible placeholder time for spacing out the row */}
              <div className='chapter-rank hidden'>
                <WithForcedMonospace>
                  00
                </WithForcedMonospace>
              </div>
              <GridWrapper>
                <GridCell className='hyphen'>
                  {NO_INFORMATION_SYMBOL}
                </GridCell>
                {/* here's the invisible placeholder time */}
                <GridCell invisible>
                  <WithForcedMonospace>
                    {trimMs(timeSpacerString || '0:00.000', msDecimals)}
                  </WithForcedMonospace>
                </GridCell>
              </GridWrapper>
            </>
          )}
        </StyledChapterTimeWrapper>
      ) : (
        // this is not a chapter leaderboard, so regardless of mode we only show the total time
        // in IL mode we might show the chapter progress instead. this component is poorly named
        // we also might be showing bingo points with this in bingo mode, teehee
        <StyledTotalWithDelta
          className='player-total'
          deltasClassName='display-flex'
          player={player}
          prependBingoPoints={prependBingoPoints}
          animatedDeltaProps={{
            shouldTripleBlink: !isFullGame,
            blinkTextAlign: 'right'
          }}
        />
      )}
    </StyledRow>
  )
})

export default SimpleBoardRow


const StyledRow = styled.div`
  position: relative;
  ${props => css`
    font-size: ${props.fontSizePx || DEFAULT_FONT_SIZE_PX}px;

    @media only screen and (max-width: ${() => `${window.phoneCutoffSizePx}px`}) and (orientation: portrait) {
      font-size: ${props.phoneFontSizePx || DEFAULT_FONT_SIZE_PX}px;
    }
  `}

  --padding-right: 0.1em;
  width: ${props => props.fullWidth ? `100%` : `calc(100% - var(--padding-right))`};
  max-width: ${props => props.fullWidth ? `100%` : `calc(100% - var(--padding-right))`};
  white-space: nowrap;
  min-width: 12em;

  display: grid;
  grid-template-columns: 1fr ${props => props.isLive ? 'min-content' : '0'} min-content;

  > * {
    grid-row: 1;
  }

  ${'' /* padding-left: 2%; */}
  padding-right: var(--padding-right);

  ${props => props.isHighlighted && css`
    background-color: var(--highlighted-player-obs-color);
    .column-right {
      color: var(--total-color);
    }
  `}

  .live-icon {
    margin-left: 0.2em;
    ${'' /* margin-right: 2%; */}
    align-self: center;
    line-height: 0;
    grid-column: 2;
    padding-top: 0.1em;
  }
  &:hover {
    background: var(--row-hover-color)!important;
  }
  &:active {
    background: var(--row-active-color)!important;
  }

  .hidden {
    visibility: hidden;
  }
`

const LeftHalf = styled.div`
  grid-column: 1;
  grid-row: 1;

  display: grid;
  margin-top: 0.08em;
  margin-bottom: 0.2em;
  font-size: 95%;

  grid-template-columns: min-content auto 1fr;
`

const Rank = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 65%;
  color: var(--rank-color);
  grid-column: 1;
  margin: 0.4em 0.25em 0 0.25em;

  ${props => {
    const width = (props.rankDigits) * 0.52
    return css`
      width: ${width}em;
      min-width: ${width}em;
      max-width: ${width}em;
      ${props.rankDigits === 0 && css`
        margin-right: 0px;
      `}
    `
  }}
`

const Name = styled.div`
  grid-column: 2;
  grid-row: 1;
  line-height: 1.29em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: auto 0;
`

const Flag = styled.div`
  height: 1.09em;
  margin: auto 0;
  > img {
    height: 70%;
    margin-left: 0.1em;
    margin-bottom: 0.23em;
  }
  grid-row: 1;
  grid-column: 3;
`

const StyledTotalWithDelta = styled(PlayerTotalAnimatedDelta)`
  grid-column: 3;
  ${'' /* max-width: 4em; */}
  ${'' /* min-width: 4em; */}
  ${'' /* width: fit-content; */}
  line-height: 1.29em;
  display: flex;
  margin-left: 0.15em;
  ${'' /* margin-right: 3em; */}
  justify-content: flex-end;
  align-items: center;
  .display-flex {
    display: flex;
    justify-content: flex-end;
  }

  .delta {
    /* text-transform: uppercase; */
    justify-content: flex-end;
  }
`

const StyledChapterTimeWrapper = styled.div`
  grid-column: 3;
  line-height: 1.29em;
  display: flex;
  margin-left: 0.15em;
  justify-content: flex-end;
  align-items: center;
  .display-flex {
    display: flex;
    justify-content: flex-end;
  }

  .delta {
    justify-content: flex-end;
  }

  .chapter-rank {
    font-size: 0.75em;
    min-width: 1.2em;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--chapter-rank-color);
    margin-top: 0.10em; 
    margin-right: 0.25em;
  }
`

const GridWrapper = styled.div`
  display: grid;
`
const GridCell = styled.div`
  grid-column: 1;
  grid-row: 1;
  text-align: right;
  &.hyphen {
    text-align: center;
    padding-right: 0.1em;
  }
  ${props => props.invisible && css`
    visibility: hidden;
  `}
`